import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { FaBolt, FaFire, FaStore } from 'react-icons/fa'
import Carousel from 'react-multi-carousel'
import 'react-multi-carousel/lib/styles.css'
import { resolveDopingBoost, trackDopingClick, useDopingImpression } from '../utilies/dopingTracking'
import { productImage } from '../utilies/imageUrl'

const getProduct = (item) => item?.productId || item
const stripHtml = (value = '') => String(value).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()

const DopingCard = ({ item, source = 'weekly_deal' }) => {
  const product = getProduct(item)
  const cardRef = useRef(null)
  const boost = product ? resolveDopingBoost(product, source, item) : null

  useDopingImpression({ ref: cardRef, product, source, boost })

  if (!product) return null

  const boosts = product.activeBoosts || [item.dopingType].filter(Boolean)
  const isWeeklyDeal = boosts.some(type => ['weekly_deal', 'gunun_firsati'].includes(type)) || product.isWeeklyDeal
  const isDailyDeal = boosts.some(type => type === 'daily_deal') || product.isDailyDeal
  const isFeatured = boosts.some(type => !['weekly_deal', 'gunun_firsati', 'daily_deal'].includes(type)) ||
    product.isFeatured || product.isMarketplaceFeatured || product.isCategoryFeatured || product.isSearchBoosted
  const isAutomatic = product.deliveryType === 'automatic'
  const stockCount = product.availableStockCount ?? product.stock
  const sellerName = product.seller?.shopName || product.seller?.name || item.sellerId?.shopInfo?.shopName || item.sellerId?.name || product.shopName || 'Satıcı məlum deyil'

  return (
    <Link
      ref={cardRef}
      to={`/product/details/${product.slug}`}
      onClick={() => trackDopingClick(product, source, boost)}
      className='group relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-lg'
    >
      {isFeatured && (
        <div className='absolute left-0 top-0 z-20 rounded-br-lg bg-violet-600 px-3 py-1 text-[11px] font-bold text-white'>
          YUXARIDA
        </div>
      )}

      <div className='relative aspect-square bg-slate-50'>
        <img
          src={productImage(product)}
          alt={`${stripHtml(product.name || 'Məhsul')} - ItemZo`}
          loading="lazy"
          decoding="async"
          className='h-full w-full object-cover transition-transform duration-300 group-hover:scale-105'
        />
        {(isWeeklyDeal || isDailyDeal) && (
          <span className='absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-orange-500 px-2.5 py-1 text-[11px] font-bold text-white shadow'>
            <FaFire /> {isDailyDeal ? 'Günün Fürsəti' : 'Fürsət'}
          </span>
        )}
        {isAutomatic && (
          <span className='absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[11px] font-bold text-white shadow'>
            <FaBolt /> Avtomatik
          </span>
        )}
      </div>

      <div className='flex flex-1 flex-col gap-2 p-3'>
        <div className='flex items-center justify-between gap-2 text-[11px] text-slate-500'>
          <span className='truncate'>{product.category || 'Kateqoriya'}</span>
          {isAutomatic && stockCount !== undefined && (
            <span className='shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700'>
              Stok {stockCount}
            </span>
          )}
        </div>

        <h3 className='min-h-[40px] text-sm font-semibold leading-5 text-slate-800 line-clamp-2 group-hover:text-orange-600'>
          {product.name}
        </h3>

        <p className='flex items-center gap-1 text-xs text-slate-500'>
          <FaStore className='text-[10px]' /> <span className='truncate'>{sellerName}</span>
        </p>

        <div className='mt-auto flex items-end justify-between gap-2'>
          <span className='text-lg font-black text-slate-900'>{product.price} ₼</span>
          {product.discount > 0 && (
            <span className='rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600'>-{product.discount}%</span>
          )}
        </div>
      </div>
    </Link>
  )
}

const DopingProductsCarousel = ({ products = [], source = 'weekly_deal' }) => {
  const responsive = {
    desktopLarge: { breakpoint: { max: 4000, min: 1440 }, items: 5 },
    desktop: { breakpoint: { max: 1440, min: 1024 }, items: 4 },
    tablet: { breakpoint: { max: 1024, min: 640 }, items: 3 },
    mobile: { breakpoint: { max: 640, min: 0 }, items: 1 }
  }

  if (!products.length) {
    return (
      <div className='rounded-lg border border-dashed border-orange-200 bg-orange-50/60 p-8 text-center text-sm text-slate-500'>
        Aktiv həftə fürsəti yoxdur.
      </div>
    )
  }

  if (products.length <= 5) {
    return (
      <div className='flex flex-wrap items-stretch justify-start gap-4'>
        {products.map((item, index) => (
          <div className='w-[210px] sm:w-full' key={item._id || getProduct(item)?._id || index}>
            <DopingCard item={item} source={source} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='weekly-deals-carousel'>
      <style>{`
        .weekly-deals-carousel .react-multi-carousel-item { padding: 0 8px; max-width: 250px; }
        .weekly-deals-carousel .react-multi-carousel-track { align-items: stretch; }
        .weekly-deals-carousel .react-multi-carousel-item > div { height: 100%; }
      `}</style>
      <Carousel responsive={responsive} infinite autoPlay autoPlaySpeed={3500} arrows swipeable draggable>
        {products.map((item, index) => (
          <div className='h-full' key={item._id || getProduct(item)?._id || index}>
            <DopingCard item={item} source={source} />
          </div>
        ))}
      </Carousel>
    </div>
  )
}

export default DopingProductsCarousel

